import React, { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/services/api";
import { Trophy, Calculator, FileText, LayoutDashboard, Layers, Loader2, Play, CheckCircle2, DollarSign, Plus } from "lucide-react";

const fmt = (n) => `$${Math.round(n || 0).toLocaleString("es-CO")}`;
const ESTADO_PLAN = { borrador: "bg-gray-100 text-gray-700", activo: "bg-green-100 text-green-800", suspendido: "bg-amber-100 text-amber-800", finalizado: "bg-red-100 text-red-700" };

export default function AdminIncentivos() {
  const [tab, setTab] = useState("resumen");
  const [resumen, setResumen] = useState(null);
  const [planes, setPlanes] = useState([]);
  const [liquidaciones, setLiquidaciones] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [planDetalle, setPlanDetalle] = useState(null);

  const load = useCallback(async () => {
    try {
      const [r, p, l, v] = await Promise.all([
        api.get("/incentivos/admin/resumen"),
        api.get("/incentivos/admin/planes"),
        api.get("/incentivos/admin/liquidaciones"),
        api.get("/usuarios"),
      ]);
      setResumen(r.data);
      setPlanes(p.data);
      setLiquidaciones(l.data);
      setVendedores(v.data.filter((u) => u.rol === "vendedor"));
    } catch (e) {
      toast.error("Error cargando Comisiones e Incentivos");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const seed = async () => {
    try {
      const res = await api.post("/incentivos/admin/seed");
      toast.success(res.data.message);
      load();
    } catch (e) { toast.error("Error en la migración inicial"); }
  };

  const tabs = [
    { id: "resumen", label: "Resumen", icon: LayoutDashboard },
    { id: "planes", label: "Planes de Remuneración", icon: Layers },
    { id: "liquidaciones", label: "Liquidaciones", icon: FileText },
    { id: "simulador", label: "Simulador", icon: Calculator },
  ];

  return (
    <div className="space-y-6" data-testid="admin-incentivos-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#4A2C0A] flex items-center gap-2">
            <Trophy className="w-6 h-6" /> Comisiones e Incentivos
          </h1>
          <p className="text-sm text-gray-600 mt-1">Motor único de remuneración variable: planes, reglas, liquidaciones y simulación</p>
        </div>
        {planes.length === 0 && (
          <Button onClick={seed} className="bg-[#4A2C0A]" data-testid="seed-planes-btn">Ejecutar Migración Inicial</Button>
        )}
      </div>

      <div className="flex gap-2 flex-wrap">
        {tabs.map((t) => (
          <Button key={t.id} variant={tab === t.id ? "default" : "outline"} className={tab === t.id ? "bg-[#4A2C0A]" : ""} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}>
            <t.icon className="w-4 h-4 mr-2" /> {t.label}
          </Button>
        ))}
      </div>

      {tab === "resumen" && resumen && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card label="Ventas netas elegibles (mes)" value={fmt(resumen.ventas_netas_elegibles)} />
            <Card label="Comisión base generada" value={fmt(resumen.comision_base)} />
            <Card label="Bonos generados" value={fmt(resumen.bonos)} color="text-amber-600" />
            <Card label="Incentivos totales" value={fmt(resumen.incentivos_totales)} color="text-green-700" />
            <Card label="Incentivos / Ventas" value={`${resumen.incentivos_sobre_ventas_pct}%`} />
            <Card label="Vendedores activos" value={resumen.vendedores_activos} />
            <Card label="Venta promedio" value={fmt(resumen.venta_promedio)} />
            <Card label="Incentivo promedio" value={fmt(resumen.incentivo_promedio)} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card label="Liq. pendientes de aprobación" value={resumen.liquidaciones.pendiente_aprobacion} color="text-amber-600" />
            <Card label="Liq. aprobadas" value={resumen.liquidaciones.aprobadas} />
            <Card label="Liq. pagadas" value={resumen.liquidaciones.pagadas} color="text-green-700" />
            <Card label="Total pagado" value={fmt(resumen.liquidaciones.total_pagado)} color="text-green-700" />
          </div>
          <div className="bg-white rounded-2xl border overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-gray-500 uppercase border-b">
                <th className="p-3">Vendedor</th><th className="p-3">Plan</th><th className="p-3">Venta neta</th><th className="p-3">Comisión SKU</th><th className="p-3">Bonos</th><th className="p-3">Total</th><th className="p-3">% s/venta</th>
              </tr></thead>
              <tbody>
                {resumen.por_vendedor.map((f) => (
                  <tr key={f.vendedor_id} className="border-b last:border-0">
                    <td className="p-3 font-medium text-[#4A2C0A]">{f.vendedor_nombre}</td>
                    <td className="p-3 text-xs">{f.plan_codigo || "—"}</td>
                    <td className="p-3">{fmt(f.venta_neta)}</td>
                    <td className="p-3">{fmt(f.comision_sku)}</td>
                    <td className="p-3">{fmt(f.bonos_diarios + f.bonos_mix + f.bono_semanal + f.bono_mensual)}</td>
                    <td className="p-3 font-bold text-green-700">{fmt(f.total)}</td>
                    <td className="p-3">{f.porcentaje_sobre_venta}%</td>
                  </tr>
                ))}
                {resumen.por_vendedor.length === 0 && <tr><td colSpan="7" className="p-6 text-center text-gray-500">Sin actividad este mes</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "planes" && (
        <div className="space-y-3">
          {planes.map((p) => (
            <div key={p.id} className="bg-white rounded-2xl border p-4" data-testid={`plan-card-${p.codigo}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="font-bold text-[#4A2C0A]">{p.codigo} — {p.nombre}</p>
                  <p className="text-xs text-gray-500">Perfil: {p.perfil} · Vigencia: {p.fecha_inicio} → {p.fecha_fin || "indefinida"} · {p.reglas?.length || 0} reglas</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={`${ESTADO_PLAN[p.estado]} border-0 capitalize`}>{p.estado}</Badge>
                  <Badge variant="outline">V{p.version}</Badge>
                  <Button size="sm" variant="outline" onClick={() => setPlanDetalle(p)} data-testid={`ver-plan-${p.codigo}`}>Ver Reglas</Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "liquidaciones" && <TabLiquidaciones liquidaciones={liquidaciones} vendedores={vendedores} onChange={load} />}
      {tab === "simulador" && <TabSimulador planes={planes} />}

      <Dialog open={!!planDetalle} onOpenChange={(o) => !o && setPlanDetalle(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{planDetalle?.codigo} — {planDetalle?.nombre}</DialogTitle>
            <DialogDescription>Versión {planDetalle?.version} · Cambios en planes activos generan nueva versión automáticamente</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {planDetalle?.reglas?.map((r) => (
              <div key={r.id} className="bg-gray-50 rounded-xl p-3">
                <p className="font-semibold text-sm text-[#4A2C0A]">{r.nombre} <span className="text-xs text-gray-400 font-mono">({r.tipo})</span> {!r.activa && <Badge className="bg-gray-200 text-gray-600 border-0 ml-1">inactiva</Badge>}</p>
                {r.tipo === "comision_sku" && (
                  <table className="w-full text-xs mt-2">
                    <thead><tr className="text-left text-gray-500"><th>Producto</th><th>Categoría</th><th>Precio ref.</th><th>Comisión</th><th>%</th></tr></thead>
                    <tbody>{r.items.map((i, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="py-1">{i.nombre_referencia || i.nombre}{!i.producto_id && <span className="text-amber-600"> ⚠ sin mapear</span>}</td>
                        <td>{i.categoria}</td><td>{fmt(i.precio_referencia)}</td>
                        <td className="font-semibold text-green-700">{fmt(i.comision)}</td>
                        <td>{i.precio_referencia ? Math.round(i.comision / i.precio_referencia * 100) : 0}%</td>
                      </tr>))}</tbody>
                  </table>
                )}
                {(r.tipo === "bono_diario_escala" || r.tipo === "bono_semanal" || r.tipo === "bono_mensual" || r.tipo === "escala_nivel") && (
                  <div className="mt-2 space-y-1 text-xs">
                    {r.niveles?.map((n, idx) => (
                      <div key={idx} className="flex justify-between bg-white rounded px-2 py-1">
                        <span>{n.nombre} — desde {fmt(n.desde)}{n.dias_activos_min ? ` (mín ${n.dias_activos_min} días activos)` : ""}</span>
                        <span className="font-semibold">{n.bono !== undefined ? fmt(n.bono) : "pendiente de configuración"}</span>
                      </div>))}
                  </div>
                )}
                {r.tipo === "bono_mix" && (
                  <p className="text-xs mt-1">Categorías: Tortas + Almojábanas/Amasijos + (Combos ó Bebidas ó Art. de la Abuela) → Bono {fmt(r.config?.bono)} / día</p>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Card = ({ label, value, color }) => (
  <div className="bg-white rounded-2xl border p-4">
    <p className={`text-xl font-bold ${color || "text-[#4A2C0A]"}`}>{value}</p>
    <p className="text-xs text-gray-500 mt-1">{label}</p>
  </div>
);

const TabLiquidaciones = ({ liquidaciones, vendedores, onChange }) => {
  const [form, setForm] = useState({ vendedor_id: "", desde: "", hasta: "" });
  const [generando, setGenerando] = useState(false);

  const generar = async () => {
    setGenerando(true);
    try {
      const res = await api.post("/incentivos/admin/liquidaciones", form);
      toast.success(`Liquidación ${res.data.numero} generada: ${fmt(res.data.total)}`);
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al generar"); }
    finally { setGenerando(false); }
  };

  const cambiarEstado = async (l, estado) => {
    try {
      await api.put(`/incentivos/admin/liquidaciones/${l.id}/estado`, { estado });
      toast.success(`Liquidación ${estado}`);
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border p-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[180px]">
          <label className="text-xs text-gray-500">Vendedor</label>
          <Select value={form.vendedor_id} onValueChange={(v) => setForm({ ...form, vendedor_id: v })}>
            <SelectTrigger data-testid="liq-vendedor-select"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
            <SelectContent>{vendedores.map((v) => <SelectItem key={v.id} value={v.id}>{v.nombre}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><label className="text-xs text-gray-500">Desde</label><Input type="date" value={form.desde} onChange={(e) => setForm({ ...form, desde: e.target.value })} data-testid="liq-desde" /></div>
        <div><label className="text-xs text-gray-500">Hasta</label><Input type="date" value={form.hasta} onChange={(e) => setForm({ ...form, hasta: e.target.value })} data-testid="liq-hasta" /></div>
        <Button onClick={generar} disabled={generando || !form.vendedor_id || !form.desde || !form.hasta} className="bg-[#4A2C0A]" data-testid="generar-liquidacion-btn">
          {generando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />} Generar Liquidación
        </Button>
      </div>
      {liquidaciones.map((l) => (
        <div key={l.id} className="bg-white rounded-2xl border p-4 flex items-center justify-between flex-wrap gap-2" data-testid={`liq-card-${l.numero}`}>
          <div>
            <p className="font-bold text-[#4A2C0A]">{l.numero} — {l.vendedor_nombre}</p>
            <p className="text-xs text-gray-500">{l.desde} → {l.hasta} · Plan {l.snapshot?.plan_codigo} V{l.snapshot?.plan_version}</p>
            <p className="text-sm mt-1">Comisión {fmt(l.snapshot?.comision_sku)} · Bonos {fmt((l.snapshot?.bonos_diarios || 0) + (l.snapshot?.bonos_mix || 0) + (l.snapshot?.bono_semanal || 0) + (l.snapshot?.bono_mensual || 0))} · <span className="font-bold text-green-700">Total {fmt(l.total)}</span></p>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={`border-0 capitalize ${l.estado === "pagada" ? "bg-green-100 text-green-800" : l.estado === "aprobada" ? "bg-blue-100 text-blue-800" : l.estado === "rechazada" ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-700"}`}>{l.estado}</Badge>
            {l.estado === "borrador" && <>
              <Button size="sm" className="bg-blue-700" onClick={() => cambiarEstado(l, "aprobada")} data-testid={`aprobar-liq-${l.numero}`}><CheckCircle2 className="w-4 h-4 mr-1" />Aprobar</Button>
              <Button size="sm" variant="outline" className="text-red-600" onClick={() => cambiarEstado(l, "rechazada")}>Rechazar</Button>
            </>}
            {l.estado === "aprobada" && <Button size="sm" className="bg-green-700" onClick={() => cambiarEstado(l, "pagada")} data-testid={`pagar-liq-${l.numero}`}><DollarSign className="w-4 h-4 mr-1" />Marcar Pagada</Button>}
          </div>
        </div>
      ))}
      {liquidaciones.length === 0 && <p className="text-center text-gray-500 py-8">No hay liquidaciones generadas</p>}
    </div>
  );
};

const TabSimulador = ({ planes }) => {
  const [planId, setPlanId] = useState("");
  const [items, setItems] = useState([{ producto_nombre: "", cantidad: 1, precio_unitario: 0 }]);
  const [resultado, setResultado] = useState(null);
  const [simulando, setSimulando] = useState(false);

  const skuItems = planes.find((p) => p.id === planId)?.reglas?.find((r) => r.tipo === "comision_sku")?.items || [];

  const simular = async () => {
    setSimulando(true);
    try {
      const res = await api.post("/incentivos/admin/simulador", { plan_id: planId, items: items.filter((i) => i.producto_nombre && i.cantidad > 0) });
      setResultado(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Error en simulación"); }
    finally { setSimulando(false); }
  };

  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="bg-white rounded-2xl border p-4 space-y-3">
        <Select value={planId} onValueChange={setPlanId}>
          <SelectTrigger data-testid="sim-plan-select"><SelectValue placeholder="Seleccionar plan..." /></SelectTrigger>
          <SelectContent>{planes.map((p) => <SelectItem key={p.id} value={p.id}>{p.codigo}</SelectItem>)}</SelectContent>
        </Select>
        {items.map((it, idx) => (
          <div key={idx} className="flex gap-2">
            <select className="flex-1 border rounded-lg h-9 text-xs px-1" value={it.producto_nombre}
              onChange={(e) => {
                const sku = skuItems.find((s) => (s.nombre_referencia || s.nombre) === e.target.value);
                setItems(items.map((x, i) => i === idx ? { ...x, producto_nombre: e.target.value, precio_unitario: sku?.precio_referencia || 0 } : x));
              }} data-testid={`sim-producto-${idx}`}>
              <option value="">Producto...</option>
              {skuItems.map((s, i) => <option key={i} value={s.nombre_referencia || s.nombre}>{s.nombre_referencia || s.nombre}</option>)}
            </select>
            <Input type="number" className="w-16" value={it.cantidad} onChange={(e) => setItems(items.map((x, i) => i === idx ? { ...x, cantidad: parseInt(e.target.value) || 0 } : x))} data-testid={`sim-cantidad-${idx}`} />
            <Input type="number" className="w-24" value={it.precio_unitario} onChange={(e) => setItems(items.map((x, i) => i === idx ? { ...x, precio_unitario: parseFloat(e.target.value) || 0 } : x))} />
          </div>
        ))}
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setItems([...items, { producto_nombre: "", cantidad: 1, precio_unitario: 0 }])}>+ Producto</Button>
          <Button size="sm" onClick={simular} disabled={simulando || !planId} className="bg-[#4A2C0A]" data-testid="simular-btn">
            {simulando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />} Simular
          </Button>
        </div>
        <p className="text-[11px] text-gray-400">Las simulaciones NO se guardan como liquidaciones reales</p>
      </div>
      {resultado && (
        <div className="bg-white rounded-2xl border p-4 space-y-2" data-testid="sim-resultado">
          <p className="text-xs text-gray-500 uppercase">Resultado — {resultado.plan_codigo}</p>
          <Row label="Venta neta" value={fmt(resultado.venta_neta)} />
          <Row label="Comisión SKU" value={fmt(resultado.comision_sku)} />
          <Row label={`Bono diario ${resultado.nivel_diario ? `(${resultado.nivel_diario})` : ""}`} value={fmt(resultado.bono_diario)} />
          <Row label="Bono Mix" value={fmt(resultado.bono_mix)} />
          <Row label="Otros incentivos" value={fmt(resultado.incentivos_extra)} />
          <div className="flex justify-between font-bold text-lg border-t pt-2 text-[#4A2C0A]"><span>Total variable</span><span className="text-green-700">{fmt(resultado.total)}</span></div>
          <Row label="Variable / venta" value={`${resultado.venta_neta ? (resultado.total / resultado.venta_neta * 100).toFixed(1) : 0}%`} />
          {resultado.explicacion?.map((e, i) => <p key={i} className="text-xs text-gray-500">• {e}</p>)}
        </div>
      )}
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex justify-between text-sm"><span className="text-gray-600">{label}</span><span className="font-semibold">{value}</span></div>
);
